import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wallet, Banknote, CreditCard, QrCode, Plus, X, CheckCircle2, Loader2 } from "lucide-react";
import { Clock } from "lucide-react";

function paymentMethodLabel(method) {
  const labels = {
    cash: "Dinheiro",
    card: "Cartão",
    bizum: "Bizum",
    mbway: "MBWay",
    pix: "Pix",
    pending: "Pendente",
  };
  return labels[method] || method;
}

function paymentMethodIcon(method) {
  const icons = {
    cash: Banknote,
    card: CreditCard,
    pending: Clock,
  };
  return icons[method] || Wallet;
}

export default function MultiPaymentModal({
  open,
  onOpenChange,
  total,
  totalPaid,
  remaining,
  multiPayments,
  newPaymentMethod,
  setNewPaymentMethod,
  newPaymentAmount,
  setNewPaymentAmount,
  onAddPayment,
  onRemovePayment,
  onConfirm,
  processing,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-purple-600" />
            Pagamento Múltiplo
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-gray-100 rounded-lg p-3">
              <p className="text-xs text-gray-500">Total</p>
              <p className="text-lg font-black text-gray-700">€{total.toFixed(2)}</p>
            </div>
            <div className="bg-emerald-100 rounded-lg p-3">
              <p className="text-xs text-emerald-600">Pago</p>
              <p className="text-lg font-black text-emerald-600">€{totalPaid.toFixed(2)}</p>
            </div>
            <div className={`rounded-lg p-3 ${remaining > 0 ? "bg-amber-100" : "bg-emerald-100"}`}>
              <p className={`text-xs ${remaining > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                Falta
              </p>
              <p
                className={`text-lg font-black ${
                  remaining > 0 ? "text-amber-600" : "text-emerald-600"
                }`}
              >
                €{remaining.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Current Payments List */}
          {multiPayments.length > 0 && (
            <div className="border rounded-lg divide-y">
              {multiPayments.map((p, idx) => {
                const PayIcon = paymentMethodIcon(p.method);
                return (
                  <div key={idx} className="flex items-center gap-3 p-3">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                      <PayIcon className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {p.label || paymentMethodLabel(p.method)}
                      </p>
                    </div>
                    <p className="text-sm font-bold">€{p.amount.toFixed(2)}</p>
                    <button
                      onClick={() => onRemovePayment(idx)}
                      className="w-6 h-6 rounded bg-red-50 flex items-center justify-center"
                    >
                      <X className="w-3 h-3 text-red-500" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add Payment Form */}
          {remaining > 0.01 && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <p className="text-sm font-semibold text-gray-700">Adicionar Pagamento</p>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { id: "cash", label: "Dinheiro", icon: Banknote, color: "emerald" },
                  { id: "card", label: "Cartão", icon: CreditCard, color: "blue" },
                  { id: "bizum", label: "Bizum", icon: Wallet, color: "cyan" },
                  { id: "pix", label: "Pix", icon: QrCode, color: "green" },
                ].map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setNewPaymentMethod(m.id)}
                    className={`p-2 rounded-lg border-2 flex flex-col items-center gap-1 transition ${
                      newPaymentMethod === m.id
                        ? `border-${m.color}-500 bg-${m.color}-50`
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <m.icon
                      className={`w-5 h-5 ${
                        newPaymentMethod === m.id ? `text-${m.color}-600` : "text-gray-400"
                      }`}
                    />
                    <span className="text-[10px] font-medium">{m.label}</span>
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Valor"
                  value={newPaymentAmount}
                  onChange={(e) => setNewPaymentAmount(e.target.value)}
                  className="flex-1 h-12 text-lg font-bold text-center"
                />
                <Button
                  onClick={() => setNewPaymentAmount(remaining.toFixed(2))}
                  variant="outline"
                  className="h-12 px-4"
                >
                  €{remaining.toFixed(2)}
                </Button>
              </div>
              <Button
                onClick={onAddPayment}
                className="w-full h-10 bg-purple-600 hover:bg-purple-700"
                disabled={!newPaymentAmount || parseFloat(newPaymentAmount) <= 0}
              >
                <Plus className="w-4 h-4 mr-1" /> Adicionar
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 pt-4 border-t flex gap-2">
          <Button
            variant="outline"
            className="flex-1 h-12"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 font-bold"
            onClick={onConfirm}
            disabled={remaining > 0.01 || processing}
          >
            {processing ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-1" />
                CONFIRMAR
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
